import React, { useCallback, useMemo, useState } from 'react';
import PageWithSideNav from 'src/components/Page/PageWithSideNav';
import { SidenavItemSection } from '@innovationnorway/component-library';
import { Button, Icon, Spinner } from '@innovationnorway/cl-react';
import AboutPage from './AboutPage';
import TargetPage from './TargetPage';
import DeliveryPage from './DeliveryPage';
import ContactInfoPage from './ContactInfoPage';
import EEAPage from './EEAPage';
import SummaryPage from './SummaryPage';
import { toast } from 'react-toastify';
import { useFormSteps } from 'src/features/siva/ideas-and-companies/hooks/use-form-steps';
import { useCluster } from 'src/features/cluster';
import {
  ClusterDto,
  ClusterUserDto,
  EmailInterval,
  OneWayInServiceDto,
  OneWayInServiceUpsertDto,
  PublicationState,
  ServiceTemplateDto,
} from 'src/api/v2';
import useOneWayInServices from 'src/features/one-way-in/hooks/use-one-way-in-services';
import { toastPromise } from 'src/utils/toast';
import { useCurrentClusterUser } from 'src/hooks/use-current-cluster-user';
import { useNavigate, useParams } from 'react-router-dom';
import useOneWayInService from '../hooks/use-service';
import { FieldErrors, UseFormSetError } from 'react-hook-form';
import { SubFormProps } from '../hooks/use-service-sub-form';
import { useTranslation } from 'react-i18next';
import useServiceTemplateById from 'src/features/one-way-in/hooks/use-service-template-by-id';
import { useUser } from 'src/features/authorization';

type PageParams = {
  serviceId?: string;
  templateId?: string;
};

function mapOneWayInServiceToUpsertDto(
  service: OneWayInServiceDto | undefined,
  cluster: ClusterDto | undefined,
  user: ClusterUserDto | undefined,
  serviceTemplate: ServiceTemplateDto | undefined,
): OneWayInServiceUpsertDto {
  const baseDefaults: OneWayInServiceUpsertDto = {
    id: undefined,
    clusterId: cluster?.id,
    businessName: cluster?.name,
    businessId: cluster?.id,
    contactName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
    contactEmail: user?.emailAddress,
    contactPhone: user?.phone,
    municipalities: [],
    externalLinks: [],
    keywords: [],
    counties: [],
    companySize: [],
    sponsors: [],
    deliveryTypes: [],
    customerSegment: [],
    selectedEeaArticles: [],
    state: PublicationState.Draft,
    description: undefined,
    customerDescription: undefined,
    name: undefined,
    internalUrl: undefined,
    servicePurpose: undefined,
    applicationDeadline: undefined,
    validFrom: undefined,
    validTo: undefined,
    districtPolicyArea: undefined,
    companyAge: undefined,
    deliveryDescription: undefined,
    hasUserFee: undefined,
    userFeeComments: undefined,
    eeaComments: undefined,
    customerSegmentComments: undefined,
    innovationLevel: undefined,
  };

  // Edit existing service
  if (service) {
    return {
      ...baseDefaults,
      ...service,
      municipalities: service.municipalities || [],
      externalLinks: service.externalLinks || [],
      keywords: service.keywords || [],
      counties: service.counties || [],
      companySize: service.companySize || [],
      sponsors: service.sponsors || [],
      deliveryTypes: service.deliveryTypes || [],
      customerSegment: service.customerSegment || [],
      selectedEeaArticles: service.selectedEeaArticles?.map((eea) => ({ eeaArticleId: eea.id })) || [],
    };
  }

  // Create from template
  if (serviceTemplate) {
    return {
      ...baseDefaults,
      ...serviceTemplate,
      id: undefined,
      keywords: serviceTemplate.keywords || [],
      externalLinks: serviceTemplate.externalLinks || [],
      deliveryTypes: serviceTemplate.deliveryTypes || [],
      sponsors: serviceTemplate.sponsors || [],
      companySize: serviceTemplate.companySizes || [],
      innovationLevel: serviceTemplate.innovationLevel || undefined,
      selectedEeaArticles: serviceTemplate.selectedEeaArticles?.map((id) => ({ eeaArticleId: id })) || [],
    };
  }

  // New service without using template
  return baseDefaults;
}

const defaultClusterUser: ClusterUserDto = {
  id: '',
  clusterId: '',
  firstName: '',
  lastName: '',
  emailAddress: '',
  phone: '22225555',
  responsibilities: '',
  organization: undefined,
  userId: undefined,
  role: undefined,
  b2CId: undefined,
  invitationStatus: undefined,
  invitationCode: undefined,
  opportunityEmailInterval: EmailInterval.Never,
  createdOn: new Date(),
};

const NewServiceForm = () => {
  const { step, stepForward, stepBackward, setStep } = useCreateServiceSteps();
  const { t: tServices } = useTranslation('services');
  const { serviceId, templateId } = useParams<PageParams>();
  const { cluster } = useCluster();
  const { service, isInitialLoading: serviceIsLoading } = useOneWayInService(serviceId || '');
  const { displayName, isAadUser } = useUser();
  const { user: clusterUser, isInitialLoading: userIsLoading } = useCurrentClusterUser(isAadUser);

  const user: ClusterUserDto = useMemo(() => {
    return (
      clusterUser ?? {
        ...defaultClusterUser,
        clusterId: cluster?.id || '',
        firstName: displayName ? displayName.split(' ')[0] : '',
        lastName: displayName ? displayName.split(' ').slice(1).join(' ') : '',
        emailAddress: `${displayName ? displayName.split(' ')[0].toLocaleLowerCase() : ''}@test.no`,
      }
    );
  }, [clusterUser, cluster?.id, displayName]);

  const [errors, setErrors] = useState<FieldErrors<Partial<OneWayInServiceUpsertDto>>>({});

  const setManualError: UseFormSetError<OneWayInServiceUpsertDto> = (name, error) => {
    setErrors((prev) => ({
      ...prev,
      [name]: { message: error, type: 'manual' },
    }));
  };

  const { upsertService, deleteService } = useOneWayInServices();

  const { serviceTemplate, isInitialLoading: templateIsLoading } = useServiceTemplateById(templateId);

  const upsertDto = useMemo(() => {
    return mapOneWayInServiceToUpsertDto(service, cluster, user, serviceTemplate);
  }, [service, cluster, user, serviceTemplate]);

  const [formOverrides, setFormOverrides] = useState<Partial<OneWayInServiceUpsertDto>>({});

  const formData = useMemo(() => ({ ...upsertDto, ...formOverrides }), [upsertDto, formOverrides]);

  const navigate = useNavigate();

  const isValid = () => Object.values(errors).every((error) => !error);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid()) {
      toast.error(tServices('errors.fillRequired'));
      return;
    }
    const record: OneWayInServiceUpsertDto = {
      ...formData,
      state: PublicationState.Active,
    };
    const promise = upsertService.mutateAsync(record);

    toastPromise(promise, {
      success: tServices('toasts.created'),
      error: tServices('toasts.error'),
    }).then(() => {
      navigate(`/services/`);
    });
  };

  const onSaveDraft = (data: OneWayInServiceUpsertDto) => {
    if (!data.name) {
      setManualError('name', {
        type: 'manual',
        message: tServices('errors.nameRequired'),
      });
      return;
    }
    const record: OneWayInServiceUpsertDto = {
      ...data,
      state: PublicationState.Draft,
    };
    const promise = upsertService.mutateAsync(record);

    toastPromise(promise, {
      success: tServices('toasts.draftSaved'),
      error: tServices('toasts.error'),
    }).then(() => {
      navigate(`/services/`);
    });
  };

  const onDelete = (data) => {
    if (!data.isDraft && !data.isActive) {
      navigate(`/services/`);
    }
    if (data.isDraft || data.isActive) {
      const promise = deleteService.mutateAsync(data.id);
      toastPromise(promise, {
        success: tServices('toasts.draftDeleted'),
        error: tServices('toasts.error'),
      }).then(() => {
        navigate(`/services/`);
      });
    }
  };

  const menu: SidenavItemSection[] = [
    {
      id: '1',
      header: `1. ${tServices('about.title')}`,
      collapsable: false,
      children: [],
      onClick: () => {
        setStep('about');
      },
      active: step === 'about',
    },
    {
      id: '2',
      header: `2. ${tServices('target.title')}`,
      collapsable: false,
      children: [],
      onClick: () => {
        setStep('target');
      },
      active: step === 'target',
    },
    {
      id: '3',
      header: `3. ${tServices('delivery.title')}`,
      collapsable: false,
      children: [],
      onClick: () => {
        setStep('delivery');
      },
      active: step === 'delivery',
    },
    {
      id: '4',
      header: `4. ${tServices('contact.title')}`,
      collapsable: false,
      children: [],
      onClick: () => {
        setStep('contactInfo');
      },
      active: step === 'contactInfo',
    },
    {
      id: '5',
      header: `5. ${tServices('eea.title')}`,
      collapsable: false,
      children: [],
      onClick: () => {
        setStep('eea');
      },
      active: step === 'eea',
    },
    {
      id: '6',
      header: `6. ${tServices('summary.title')}`,
      collapsable: false,
      children: [],
      onClick: () => {
        setStep('summary');
      },
      active: step === 'summary',
    },
  ];

  const updateData = useCallback(<T extends Record<string, any>>(newData: T) => {
    setFormOverrides((prev) => {
      const next = { ...prev, ...newData };
      return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
    });
  }, []);

  const commonProps: SubFormProps<Partial<OneWayInServiceUpsertDto>> = {
    data: formData,
    updateData,
    errors,
    setErrors,
  };

  if (serviceIsLoading || templateIsLoading || userIsLoading)
    return (
      <div className="display--flex justify-content--center">
        <Spinner />
      </div>
    );

  return (
    <PageWithSideNav basis={15} menu={menu}>
      <div className="display--flex flex-direction--row service--page gap--4">
        <div data-size="md">
          <form onSubmit={onSubmit}>
            {(() => {
              switch (step) {
                case 'about':
                  return <AboutPage {...commonProps} />;
                case 'target':
                  return <TargetPage {...commonProps} />;
                case 'delivery':
                  return <DeliveryPage {...commonProps} />;
                case 'contactInfo':
                  return <ContactInfoPage {...commonProps} />;
                case 'eea':
                  return <EEAPage {...commonProps} />;
                case 'summary':
                  return <SummaryPage {...commonProps} />;
                default:
                  return null;
              }
            })()}
            <div className="display--flex gap--1 margin-top--3 max-width--100">
              <div className="display--flex flex-direction--row gap--1">
                {step !== 'about' && (
                  <Button variant="secondary" type="button" onClick={stepBackward}>
                    {tServices('form.back')}
                  </Button>
                )}
                {step !== 'summary' && (
                  <Button type="button" onClick={stepForward}>
                    {tServices('form.next')}
                  </Button>
                )}
                {step === 'summary' && (
                  <Button
                    type="submit"
                    disabled={!isValid() || upsertService.isLoading || deleteService.isLoading}
                  >
                    {service?.state === PublicationState.Active
                      ? tServices('form.publishChanges')
                      : tServices('form.publish')}
                  </Button>
                )}
              </div>
              <div className="display--flex flex-direction--row gap--1 margin-left--auto">
                <Button
                  variant="tertiary"
                  disabled={deleteService.isLoading || upsertService.isLoading}
                  onClick={() => onDelete(formData)}
                >
                  <Icon prefix="fass" iconName="trash-can" />
                  {tServices('form.deleteDraft')}
                </Button>
                <Button
                  variant="tertiary"
                  disabled={upsertService.isLoading || deleteService.isLoading}
                  onClick={() => onSaveDraft(formData)}
                >
                  <Icon prefix="fass" iconName="floppy-disk" />
                  {tServices('form.saveAsDraft')}
                </Button>
              </div>
            </div>
          </form>
        </div>
        <div className="service-template-fakedoor"></div>
      </div>
    </PageWithSideNav>
  );
};

export default NewServiceForm;

function useCreateServiceSteps() {
  const steps: ReadonlyArray<string> = ['about', 'target', 'delivery', 'contactInfo', 'eea', 'summary'];
  return useFormSteps(steps);
}
